import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, money, pct, parseAR, formatAR } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Save, FileText, Plus, X, RotateCcw, Lock, Eye, Download } from "lucide-react";
import { DateField } from "@/components/DatePickers";
import { StaleDataDialog } from "@/components/StaleDataDialog";
import { CrossReplaceDialog } from "@/components/CrossReplaceDialog";
import { useFeedbackGate } from "@/components/FeedbackGate";

const emptyMonths = () => [{ rem: "", nr: "" }];
const fdate = (s) => (s ? s.split("-").reverse().join("/") : "—");
const fmtFechaISO = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const NOTAS = [
  "MARCAS (celdas naranjas): seleccionables / editables por el usuario.",
  "Contribución seg. social: 18% (inc. b, resto/PyME) o 20,40% (inc. a, Comercio/Servicios — art. 19 Ley 27.541). El desglose por subsistema es referencial (el F.931 declara el total unificado).",
  "¿La parte no remunerativa genera Obra Social?: si 'Sí', el SAC no remunerativo se suma a la base del aporte (3%) y la contribución (6%) de obra social.",
  "Mejor Remuneración: cargá la mayor remuneración MENSUAL TOTAL del semestre (remun + NR); sobre esa composición se calcula el SAC.",
  "Bases imponibles (Res. ANSES 139/2026): valores MENSUALES; se computan al coeficiente y se prorratean por días.",
  "Aportes seg. social (Jub., PAMI): base con tope mín-máx. Retenciones sindicales (FAECYS y SEC): base = SAC total, sin topes.",
  "Detracción Dto. 814/2001: se resta de la base de los 4 subsistemas (no de obra social), prorrateada por el factor del SAC.",
];

export default function CalculatorPage() {
  const [sp] = useSearchParams();
  const { calcId } = useParams();
  const [legajos, setLegajos] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [staleParams, setStaleParams] = useState(null);
  const [form, setForm] = useState({
    legajoId: sp.get("legajo") || "", nombre: "Aguinaldo", semestreYear: new Date().getFullYear(),
    semestreHalf: 1, fechaIngreso: "", fechaEgreso: "", tierEmpleador: "18",
    nrGeneraOS: true, months: emptyMonths(),
    tipoConvenio: "comercio", retencionSindicalOtro: "", aportesExtra: [], contribucionesExtra: [],
    diasTrabajadosOverride: "", baseObraSocialOverride: "", baseObraSocial6Override: "", baseArtOverride: "", ratesEmpleadorOverride: {}, contribExcluidas: [],
    coeficienteSAC: 0.5, baseMinima: "", baseMaxima: "", detraccionMensual: "",
    ratesEmpleado: { sipa: "", pami: "", osecac: "", faecys: "", sec: "" }, obraSocialEmpleador: "",
  });
  const [legajoSel, setLegajoSel] = useState(null);
  const [result, setResult] = useState(null);
  const [savedCalcId, setSavedCalcId] = useState(null);
  const [locked, setLocked] = useState(false);
  const [receiptId, setReceiptId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [diasEdited, setDiasEdited] = useState(false);
  const [osBaseEdited, setOsBaseEdited] = useState(false);
  const [os6BaseEdited, setOs6BaseEdited] = useState(false);
  const [artBaseEdited, setArtBaseEdited] = useState(false);
  const [s6cfg, setS6cfg] = useState(null);
  const readOnly = sp.get("view") === "1" || locked;
  const { guard } = useFeedbackGate();
  const deepLinkDone = useRef(false);

  useEffect(() => { api.get("/legajos").then((r) => setLegajos(r.data)).catch(() => {}); }, []);
  const [usageInfo, setUsageInfo] = useState({ enabled: false, usage: {} });
  const [crossSel, setCrossSel] = useState(null);
  const fetchUsage = () => api.get("/legajos/usage-status").then((r) => setUsageInfo(r.data)).catch(() => {});
  useEffect(() => { fetchUsage(); }, []);
  useEffect(() => { api.get("/seccion6-config").then((r) => setS6cfg(r.data)).catch(() => {}); }, []);

  // parámetros según el período liquidado: ancla 30/06 (Sem 1) o 31/12 (Sem 2)
  useEffect(() => {
    const y = Number(form.semestreYear);
    if (!y || y < 1990 || y > 2100) return;
    const anchor = Number(form.semestreHalf) === 1 ? `${y}-06-30` : `${y}-12-31`;
    api.get(`/params/por-fecha?fecha=${anchor}`).then((r) => {
      const d = r.data; setDefaults(d);
      if (!calcId) setForm((f) => ({
        ...f, coeficienteSAC: d.coeficienteSAC, baseMinima: formatAR(d.baseMinima), baseMaxima: formatAR(d.baseMaxima),
        detraccionMensual: formatAR(d.detraccionMensual), ratesEmpleado: { ...d.ratesEmpleado }, obraSocialEmpleador: d.obraSocialEmpleador,
      }));
    }).catch(() => {});
  }, [calcId, form.semestreYear, form.semestreHalf]);

  const loadSaved = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/calculations/${id}`);
      const i = data.inputs;
      setForm((f) => ({ ...f, ...i, legajoId: i.legajoId || "", fechaIngreso: i.fechaIngreso || "", fechaEgreso: i.fechaEgreso || "",
        tipoConvenio: i.tipoConvenio || "comercio", retencionSindicalOtro: i.retencionSindicalOtro || "",
        aportesExtra: (i.aportesExtra || []).map((l) => ({ concepto: l.concepto || "", base: formatAR(l.base), alicuota: l.alicuota, visibleCalc: l.visibleCalc !== false, editable: l.editable !== false, visiblePdf: l.visiblePdf !== false })),
        contribucionesExtra: (i.contribucionesExtra || []).map((l) => ({ concepto: l.concepto || "", base: formatAR(l.base), alicuota: l.alicuota, visibleCalc: l.visibleCalc !== false, editable: l.editable !== false, visiblePdf: l.visiblePdf !== false })),
        baseMinima: formatAR(i.baseMinima), baseMaxima: formatAR(i.baseMaxima), detraccionMensual: formatAR(i.detraccionMensual),
        baseObraSocialOverride: i.baseObraSocialOverride != null ? formatAR(i.baseObraSocialOverride) : "",
        baseObraSocial6Override: i.baseObraSocial6Override != null ? formatAR(i.baseObraSocial6Override) : "",
        baseArtOverride: i.baseArtOverride != null ? formatAR(i.baseArtOverride) : "",
        ratesEmpleado: { ...f.ratesEmpleado, ...(i.ratesEmpleado || {}) },
        months: (() => {
          const ms = i.months || [];
          const best = ms.reduce((a, b) => ((Number(b.rem) || 0) + (Number(b.nr) || 0) > (Number(a.rem) || 0) + (Number(a.nr) || 0) ? b : a), ms[0] || { rem: 0, nr: 0 });
          return [{ rem: formatAR(best.rem), nr: formatAR(best.nr) }];
        })() }));
      setResult(data.resultado); setSavedCalcId(data.id); setDiasEdited(i.diasTrabajadosOverride != null);
      setOsBaseEdited(i.baseObraSocialOverride != null);
      setOs6BaseEdited(i.baseObraSocial6Override != null);
      setArtBaseEdited(i.baseArtOverride != null);
      setLocked(!!data.locked); setReceiptId(data.receiptId || null);
    } catch { toast.error("No se pudo cargar el cálculo"); }
  }, []);

  useEffect(() => { if (calcId) loadSaved(calcId); }, [calcId, loadSaved]);

  // Seed the "Días trabajados" field with the computed value when results arrive,
  // unless the user has manually edited it (single source of truth = diasTrabajadosOverride).
  useEffect(() => {
    if (result?.seccion3 && !diasEdited) {
      setForm((f) => ({ ...f, diasTrabajadosOverride: String(result.seccion3.diasTrabajados) }));
    }
  }, [result, diasEdited]);

  // Seed the editable "Base imponible" of Obra social (OSECAC) with the computed value
  // when results arrive, unless the user has manually edited it.
  useEffect(() => {
    if (result?.aportes && result.baseObraSocial != null && !osBaseEdited) {
      setForm((f) => ({ ...f, baseObraSocialOverride: formatAR(result.baseObraSocial) }));
    }
  }, [result, osBaseEdited]);

  // Seed the editable "Base imponible" of A.R.T. with the DEFAULT base (= Aguinaldo Bruto
  // Total) when results arrive, unless the user has manually edited it. Using the default
  // (not the applied/overridden base) ensures the ↺ reset restores the original value.
  useEffect(() => {
    if (result?.contribuciones && result.contribuciones.artBaseDefault != null && !artBaseEdited) {
      setForm((f) => ({ ...f, baseArtOverride: formatAR(result.contribuciones.artBaseDefault) }));
    }
  }, [result, artBaseEdited]);

  // Seed the editable "Base imponible" of Obra social (Sección 6, patronal) from the default
  // base when results arrive, unless the user edited it (so ↺ reset restores the original).
  useEffect(() => {
    if (result?.contribuciones && result.contribuciones.obraSocial6BaseDefault != null && !os6BaseEdited) {
      setForm((f) => ({ ...f, baseObraSocial6Override: formatAR(result.contribuciones.obraSocial6BaseDefault) }));
    }
  }, [result, os6BaseEdited]);


  useEffect(() => {
    const lg = legajos.find((l) => l.id === form.legajoId);
    setLegajoSel(lg || null);
    // Prefill once for deep-link (?legajo=) on a NEW calc; never clobber saved/edited values
    if (lg && !calcId && !deepLinkDone.current && !form.fechaIngreso) {
      deepLinkDone.current = true;
      setForm((f) => ({ ...f, fechaIngreso: lg.fechaIngreso || "", nombre: `Aguinaldo ${lg.nombre}`, tipoConvenio: lg.tipoConvenio || f.tipoConvenio }));
    }
  }, [form.legajoId, legajos, calcId]);

  // User picks a different employee → refresh all employee-derived fields
  const selectLegajo = (v) => {
    const id = v === "none" ? "" : v;
    const u = usageInfo.enabled && id && id !== form.legajoId ? usageInfo.usage[id] : null;
    if (u === "sueldo" || u === "ambos") { setCrossSel(v); return; }
    applyLegajo(v);
  };
  const applyLegajo = (v) => {
    const id = v === "none" ? "" : v;
    const lg = legajos.find((l) => l.id === id);
    setForm((f) => ({
      ...f,
      legajoId: id,
      fechaIngreso: lg ? (lg.fechaIngreso || "") : "",
      tipoConvenio: lg?.tipoConvenio || "comercio",
      tierEmpleador: lg?.empresa?.contribSegSocial || f.tierEmpleador,
      retencionSindicalOtro: "",
      nombre: lg ? `Aguinaldo ${lg.nombre}` : f.nombre,
    }));
  };

  const payload = () => ({
    ...form, semestreYear: Number(form.semestreYear), semestreHalf: Number(form.semestreHalf),
    paramsVersion: defaults?.version ?? null,
    fechaIngreso: form.fechaIngreso || null, fechaEgreso: form.fechaEgreso || null, legajoId: form.legajoId || null,
    coeficienteSAC: Number(form.coeficienteSAC || 0),
    retencionSindicalOtro: Number(form.retencionSindicalOtro || 0),
    diasTrabajadosOverride: diasEdited && form.diasTrabajadosOverride !== "" && form.diasTrabajadosOverride != null ? Number(form.diasTrabajadosOverride) : null,
    baseObraSocialOverride: osBaseEdited && form.baseObraSocialOverride !== "" && form.baseObraSocialOverride != null ? parseAR(form.baseObraSocialOverride) : null,
    baseObraSocial6Override: os6BaseEdited && form.baseObraSocial6Override !== "" && form.baseObraSocial6Override != null ? parseAR(form.baseObraSocial6Override) : null,
    baseArtOverride: artBaseEdited && form.baseArtOverride !== "" && form.baseArtOverride != null ? parseAR(form.baseArtOverride) : null,
    ratesEmpleadorOverride: Object.keys(form.ratesEmpleadorOverride || {}).length ? form.ratesEmpleadorOverride : null,
    contribExcluidas: form.contribExcluidas || [],
    aportesExtra: (form.aportesExtra || []).map((l) => ({ concepto: l.concepto || "", base: parseAR(l.base), alicuota: Number(l.alicuota || 0), visibleCalc: l.visibleCalc !== false, editable: l.editable !== false, visiblePdf: l.visiblePdf !== false })),
    contribucionesExtra: (form.contribucionesExtra || []).map((l) => ({ concepto: l.concepto || "", base: parseAR(l.base), alicuota: Number(l.alicuota || 0), visibleCalc: true, editable: true, visiblePdf: true })),
    baseMinima: parseAR(form.baseMinima), baseMaxima: parseAR(form.baseMaxima), detraccionMensual: parseAR(form.detraccionMensual),
    obraSocialEmpleador: Number(form.obraSocialEmpleador || 0),
    ratesEmpleado: Object.fromEntries(Object.entries(form.ratesEmpleado).map(([k, v]) => [k, Number(v || 0)])),
    months: form.months.map((m) => ({ rem: parseAR(m.rem), nr: parseAR(m.nr) })),
  });

  const sec1Ok = (form.legajoId || form.nombre) && form.semestreYear && form.fechaIngreso;
  const sec2Ok = form.months.some((m) => parseAR(m.rem) > 0 || parseAR(m.nr) > 0);
  const missingSection = () => {
    if (!sec1Ok) return "Sección 1 (Datos del cálculo)";
    if (!sec2Ok) return "Sección 2 (Mejor remuneración del semestre)";
    if (!result) return "Sección 3 a 6 (ejecute el cálculo)";
    return null;
  };

  const handle409 = (err) => {
    const d = err.response?.data?.detail;
    if (err.response?.status === 409 && d?.code === "PARAMS_UPDATED") { setStaleParams(d.fresh); return true; }
    return false;
  };

  const staleRows = staleParams ? (() => {
    const rows = [
      ["Base imponible mínima", form.baseMinima, staleParams.baseMinima],
      ["Base imponible máxima", form.baseMaxima, staleParams.baseMaxima],
      ["Detracción mensual Dto. 814", form.detraccionMensual, staleParams.detraccionMensual],
    ].filter(([, o, n]) => parseAR(o) !== Number(n)).map(([label, o, n]) => ({ label, old: money(parseAR(o)), nue: money(Number(n)) }));
    if (Number(form.obraSocialEmpleador || 0) !== Number(staleParams.obraSocialEmpleador || 0) ||
        JSON.stringify(form.ratesEmpleado) !== JSON.stringify(staleParams.ratesEmpleado)) {
      rows.push({ text: "Alícuotas de aportes / obra social empleador actualizadas" });
    }
    if (!rows.length) rows.push({ text: "Otros parámetros del período actualizados" });
    return rows;
  })() : [];

  const applyFreshParams = async () => {
    const f = staleParams;
    setForm((prev) => ({ ...prev, baseMinima: formatAR(f.baseMinima), baseMaxima: formatAR(f.baseMaxima),
      detraccionMensual: formatAR(f.detraccionMensual), ratesEmpleado: { ...f.ratesEmpleado },
      obraSocialEmpleador: f.obraSocialEmpleador, coeficienteSAC: f.coeficienteSAC }));
    setDefaults(f); setStaleParams(null); setLoading(true);
    try {
      const { data } = await api.post("/calculations/preview", { ...payload(), baseMinima: f.baseMinima, baseMaxima: f.baseMaxima,
        detraccionMensual: f.detraccionMensual, ratesEmpleado: f.ratesEmpleado, obraSocialEmpleador: f.obraSocialEmpleador,
        paramsVersion: f.version });
      setResult(data.resultado); toast.success("Cálculo actualizado con los parámetros nuevos");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };

  const calcular = async () => {
    if (!sec1Ok || !sec2Ok) { toast.error("Datos incompletos: complete Sección 1 y Sección 2 antes de calcular"); return; }
    setLoading(true);
    try { const { data } = await api.post("/calculations/preview", payload()); setResult(data.resultado); toast.success("Cálculo realizado"); }
    catch (err) { if (!handle409(err)) toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  const guardar = async () => {
    if (!sec1Ok || !sec2Ok) { toast.error("Datos incompletos: complete Sección 1 y Sección 2 antes de guardar"); return; }
    try {
      const id = calcId || savedCalcId;
      const { data } = id ? await api.put(`/calculations/${id}`, payload()) : await api.post("/calculations", payload());
      setSavedCalcId(data.id); setResult(data.resultado);
      toast.success(data.replacedSueldo ? "Cálculo guardado — se reemplazó el Recibo de Sueldo anterior de este empleado. Ya podés generar el recibo." : "Cálculo guardado. Ya podés generar el recibo.");
      fetchUsage();
    } catch (err) { if (!handle409(err)) toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const verRecibo = async (rid) => {
    try {
      await guard(async () => {
        const { data } = await api.get(`/receipts/${rid}/pdf?action=view`, { responseType: "blob" });
        window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
      });
    } catch { toast.error("No se pudo abrir el PDF"); }
  };
  const descargarRecibo = async (rid) => {
    try {
      await guard(async () => {
        const { data } = await api.get(`/receipts/${rid}/pdf?action=downloaded`, { responseType: "blob" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        a.download = `recibo_${rid}.pdf`; a.click();
        toast.success("Recibo descargado");
      });
    } catch { toast.error("Error al descargar"); }
  };
  const generarRecibo = async () => {
    if (locked && receiptId) return verRecibo(receiptId);
    const miss = missingSection();
    if (miss) { toast.error(`Datos incompletos: ${miss}`); return; }
    try {
      const id = calcId || savedCalcId;
      const { data: calc } = id ? await api.put(`/calculations/${id}`, payload()) : await api.post("/calculations", payload());
      setSavedCalcId(calc.id); setResult(calc.resultado);
      const { data: rec } = await api.post("/receipts", { calculationId: calc.id });
      setReceiptId(rec.id); setLocked(true);
      toast.success("Recibo generado", { action: { label: "Ver recibo", onClick: () => verRecibo(rec.id) } });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setMonth = (i, key, val) => { const months = [...form.months]; months[i] = { ...months[i], [key]: val }; setForm({ ...form, months }); };
  const setRate = (k, v) => setForm({ ...form, ratesEmpleado: { ...form.ratesEmpleado, [k]: v } });
  const addExtra = (key) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), { concepto: "", base: "", alicuota: "", visibleCalc: true, editable: true, visiblePdf: true }] }));
  const setExtra = (key, i, k, v) => setForm((f) => { const a = [...f[key]]; a[i] = { ...a[i], [k]: v }; return { ...f, [key]: a }; });
  const delExtra = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, j) => j !== i) }));
  const setPatRate = (k, v) => setForm((f) => ({ ...f, ratesEmpleadorOverride: { ...f.ratesEmpleadorOverride, [k]: v } }));
  const excludeContrib = (k) => setForm((f) => ({ ...f, contribExcluidas: [...(f.contribExcluidas || []), k] }));
  const cfg6 = (k) => (s6cfg && s6cfg.items && s6cfg.items[k]) || { visibleCalc: true, editable: true, visiblePdf: true };
  const recalcDias = async () => {
    setDiasEdited(false);
    if (!sec1Ok || !sec2Ok) { setForm((f) => ({ ...f, diasTrabajadosOverride: "" })); return; }
    try {
      const { data } = await api.post("/calculations/preview", { ...payload(), diasTrabajadosOverride: null });
      setResult(data.resultado);
      setForm((f) => ({ ...f, diasTrabajadosOverride: String(data.resultado.seccion3.diasTrabajados) }));
      toast.success("Días recalculados");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const s3 = result?.seccion3, s4 = result?.seccion4, ap = result?.aportes, co = result?.contribuciones;
  const patRates = { ...(defaults?.ratesEmpleador?.[form.tierEmpleador] || {}), ...(form.ratesEmpleadorOverride || {}) };
  const ovr = form.ratesEmpleadorOverride || {};
  const artRate = ovr.art != null ? ovr.art : (co?.artRate ?? defaults?.art);
  const scvoRate = ovr.scvo != null ? ovr.scvo : (co?.scvoRate ?? defaults?.scvo);

  return (
    <AppLayout title="Aguinaldo"
      actions={
        <div className="flex gap-2 items-center">
          {locked && <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-300 font-medium" data-testid="calc-locked-badge"><Lock size={12} /> Solo lectura — recibo emitido</span>}
          {!locked && <Button onClick={calcular} disabled={loading || readOnly || !sec1Ok || !sec2Ok} data-testid="calc-run" className="rounded-none brand-bg text-white gap-2"><Play size={15} /> Calcular</Button>}
          {!locked && <Button onClick={guardar} disabled={readOnly} variant="outline" data-testid="calc-save" className="rounded-none gap-2"><Save size={15} /> Guardar</Button>}
          {locked && receiptId ? (
            <>
              <Button onClick={() => verRecibo(receiptId)} variant="outline" data-testid="calc-view-receipt" className="rounded-none gap-2"><Eye size={15} /> Ver recibo</Button>
              <Button onClick={() => descargarRecibo(receiptId)} variant="outline" data-testid="calc-download-receipt" className="rounded-none gap-2"><Download size={15} /> Descargar</Button>
            </>
          ) : (
            <Button onClick={generarRecibo} disabled={readOnly} variant="outline" data-testid="calc-receipt" className="rounded-none gap-2"><FileText size={15} /> Generar recibo</Button>
          )}
        </div>
      }>
      <fieldset disabled={readOnly} className="max-w-5xl mx-auto space-y-px bg-border border border-border min-w-0 w-full p-0 m-0 disabled:opacity-100">
        {readOnly && (
          <div className="bg-amber-100 border border-amber-300 text-amber-900 text-sm px-4 py-2 font-medium" data-testid="calc-readonly-banner">
            Modo lectura — solo visualización. Para modificar, use el botón Editar en la Lista.
          </div>
        )}

        {/* SECCIÓN 1 */}
        <Sec n="1" title="Datos y parámetros del cálculo">
          <div className="grid md:grid-cols-3 gap-4 items-stretch [&>div]:flex [&>div]:flex-col [&>div]:justify-end">
            <div className="md:col-span-3">
              <Label className="label-tiny">Empleado (Legajo)</Label>
              <Select value={form.legajoId || "none"} onValueChange={selectLegajo}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="calc-legajo"><SelectValue placeholder="Sin legajo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin legajo</SelectItem>
                  {legajos.map((l) => {
                    const u = usageInfo.enabled && l.id !== form.legajoId ? usageInfo.usage[l.id] : null;
                    return <SelectItem key={l.id} value={l.id}>{l.legajoNumero ? `${l.legajoNumero} · ` : ""}{l.nombre}{u === "sueldo" || u === "ambos" ? " — tiene Sueldo (se reemplaza al guardar)" : u === "aguinaldo" ? " — tiene Aguinaldo (se reemplaza al guardar)" : ""}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <ReadF label="Apellido y Nombre" value={legajoSel?.nombre} testid="f-nombre" />
            <ReadF label="CUIL" value={legajoSel?.cuil} testid="f-cuil" />
            <ReadF label="Legajo N°" value={legajoSel?.legajoNumero} testid="f-numero" />
            <div>
              <Label className="label-tiny">Fecha de ingreso</Label>
              <DateField iso testid="calc-ingreso" className="mt-1.5 xls-input w-full" value={form.fechaIngreso || ""} onChange={(v) => setForm({ ...form, fechaIngreso: v })} />
            </div>
            <div>
              <Label className="label-tiny">Año a liquidar</Label>
              <Input type="number" data-testid="calc-year" className="mt-1.5 xls-input" value={form.semestreYear} onChange={(e) => setForm({ ...form, semestreYear: e.target.value })} />
            </div>
            <div>
              <Label className="label-tiny">Semestre</Label>
              <Select value={String(form.semestreHalf)} onValueChange={(v) => setForm({ ...form, semestreHalf: Number(v) })}>
                <SelectTrigger className="mt-1.5 xls-input" data-testid="calc-semestre"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">1 (ene-jun)</SelectItem><SelectItem value="2">2 (jul-dic)</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tiny">Tipo CCT (convenio)</Label>
              <Select value={form.tipoConvenio} onValueChange={(v) => setForm({ ...form, tipoConvenio: v })}>
                <SelectTrigger className="mt-1.5 xls-input" data-testid="calc-tipoconvenio"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="comercio">Comercio (CCT 130/75)</SelectItem>
                  <SelectItem value="fuera_convenio">Fuera de convenio</SelectItem>
                  <SelectItem value="otro_convenio">Otro convenio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tiny">% retención sindical (solo 'Otro Convenio')</Label>
              <div className="flex items-center gap-1 mt-1.5">
                <ARPctInput value={form.retencionSindicalOtro} onChange={(v) => setForm({ ...form, retencionSindicalOtro: v })} testid="calc-retencion-otro" disabled={form.tipoConvenio !== "otro_convenio"} className="xls-input text-right disabled:opacity-50 disabled:bg-slate-100" />
                <span className="text-slate-500">%</span>
              </div>
            </div>
            <div className="hidden md:block" aria-hidden="true" />
            <div>
              <Label className="label-tiny">Fecha de egreso (vacío si continúa)</Label>
              <DateField iso testid="calc-egreso" className="mt-1.5 xls-input w-full" value={form.fechaEgreso || ""} onChange={(v) => setForm({ ...form, fechaEgreso: v })} />
            </div>
            <div>
              <Label className="label-tiny">Contribución seg. social</Label>
              <Select value={form.tierEmpleador} onValueChange={(v) => setForm({ ...form, tierEmpleador: v })}>
                <SelectTrigger className="mt-1.5 xls-input" data-testid="calc-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="18">18% (resto/PyME)</SelectItem>
                  <SelectItem value="20.40">20,40% (inc. a, Comercio/Servicios)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tiny">¿La parte no remunerativa genera Obra Social?</Label>
              <Select value={form.nrGeneraOS ? "si" : "no"} onValueChange={(v) => setForm({ ...form, nrGeneraOS: v === "si" })}>
                <SelectTrigger className="mt-1.5 xls-input" data-testid="calc-nr-os"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="si">Sí</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        </Sec>

        {/* SECCIÓN 2 */}
        <Sec n="2" title="Mejor remuneración del semestre">
          <div className="grid grid-cols-[1.2fr_1fr_1fr_1fr] gap-2 label-tiny mb-2">
            <span /><span>Remuneración Bruta</span><span>Remuneración sin descuentos</span><span>Total mensual ($)</span>
          </div>
          <div className="grid grid-cols-[1.2fr_1fr_1fr_1fr] gap-2 mb-2 items-center">
            <span className="text-sm text-slate-600">Mejor Remuneración</span>
            <ARMoneyInput testid="mejor-rem" value={form.months[0]?.rem ?? ""} onChange={(v) => setMonth(0, "rem", v)} compact />
            <ARMoneyInput testid="mejor-nr" value={form.months[0]?.nr ?? ""} onChange={(v) => setMonth(0, "nr", v)} compact />
            <span className="text-sm tabular text-slate-900 px-2" data-testid="mejor-total">{money(parseAR(form.months[0]?.rem) + parseAR(form.months[0]?.nr))}</span>
          </div>
        </Sec>

        {/* SECCIÓN 3 — orden exacto del Excel */}
        <Sec n="3" title="Cálculo del aguinaldo bruto">
          <div className="flex flex-col" data-testid="calc-result">
            <KV l="Mejor remuneración mensual (total)" v={s3 ? money(s3.mejorRemuneracion) : "—"} />
            <KV l="→ parte remunerativa (base de aportes)" v={s3 ? money(s3.parteRemunerativa) : "—"} />
            <KV l="→ parte no remunerativa" v={s3 ? money(s3.parteNoRemunerativa) : "—"} />
            <KVedit l="Coeficiente SAC">
              <PctInput testid="coef-sac" value={form.coeficienteSAC} onChange={(v) => setForm({ ...form, coeficienteSAC: v })} />
            </KVedit>
            <KV l="Inicio del semestre" v={s3 ? fdate(s3.inicioSemestre) : "—"} />
            <KV l="Fin del semestre" v={s3 ? fdate(s3.finSemestre) : "—"} />
            <KV l="Días del semestre" v={s3 ? s3.diasSemestre : "—"} />
            <KV l="Inicio de cómputo" v={s3 ? fdate(s3.inicioComputo) : "—"} />
            <KV l="Fin de cómputo" v={s3 ? fdate(s3.finComputo) : "—"} />
            <KVedit l="Días trabajados en el semestre">
              <div className="flex items-center gap-1.5">
                <Input type="number" data-testid="dias-trabajados" className="xls-input text-right w-24"
                  value={form.diasTrabajadosOverride ?? ""}
                  onChange={(e) => { setDiasEdited(true); setForm({ ...form, diasTrabajadosOverride: e.target.value }); }} />
                <button type="button" onClick={recalcDias} title="Recalcular días" data-testid="dias-recalc" className="text-slate-500 hover:brand-text"><RotateCcw size={15} /></button>
              </div>
            </KVedit>
            <KV l="Proporción del semestre" v={s3 ? pct(s3.proporcion) : "—"} />
            <KV l="SAC sobre remuneración (real, sin topes)" v={s3 ? money(s3.sacSobreRemuneracion) : "—"} />
            <KV l="SAC sobre conceptos no remunerativos" v={s3 ? money(s3.sacSobreNR) : "—"} />
            <div className="w-full brand-bg text-white flex justify-between px-3 py-2.5 mt-2" data-testid="calc-bruto">
              <span className="label-tiny label-on-dark">AGUINALDO BRUTO (TOTAL)</span>
              <span className="font-head font-bold tabular text-lg">{s3 ? money(s3.aguinaldoBruto) : "—"}</span>
            </div>
          </div>
        </Sec>

        {/* SECCIÓN 4 */}
        <Sec n="4" title="Bases imponibles (Res. ANSES y Dto. 814)">
          {defaults?.validoDesde && (
            <p className="text-xs text-slate-600 mb-3" data-testid="params-validez-calc">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 align-middle" />
              Parámetros aplicados: <b>{Number(form.semestreHalf) === 1 ? "Junio" : "Diciembre"} {form.semestreYear}</b> (Semestre {form.semestreHalf}) — Válido desde <b>{defaults.validoDesde}</b> · hasta <b>{defaults.validoHasta || "—"}</b>{" "}
              <span className="text-muted-foreground">(los valores se pueden editar abajo)</span>
              {defaults.fallback && (
                <span className="block text-amber-700 mt-1" data-testid="params-fallback-warning">
                  ⚠ No hay una versión de parámetros que cubra ese período: se aplica la versión más cercana disponible. Verificá los valores.
                </span>
              )}
            </p>
          )}
          <div className="flex flex-col">
            <KVedit l="Base imponible mínima MENSUAL"><MoneyInput testid="base-min" value={form.baseMinima} onChange={(v) => setForm({ ...form, baseMinima: v })} /></KVedit>
            <KVedit l="Base imponible máxima MENSUAL"><MoneyInput testid="base-max" value={form.baseMaxima} onChange={(v) => setForm({ ...form, baseMaxima: v })} /></KVedit>
            <KVedit l="Detracción Dto. 814 MENSUAL"><MoneyInput testid="detraccion" value={form.detraccionMensual} onChange={(v) => setForm({ ...form, detraccionMensual: v })} /></KVedit>
            <KV l="Factor aplicable al SAC (coef. × proporción)" v={s4 ? pct(s4.factorSAC) : "—"} />
            <KV l="Base mínima del SAC" v={s4 ? money(s4.baseMinSAC) : "—"} />
            <KV l="Base máxima del SAC" v={s4 ? money(s4.baseMaxSAC) : "—"} />
            <KV l="Detracción Dto. 814 del SAC (prorrateada)" v={s4 ? money(s4.detraccionSAC) : "—"} />
            <KV l="NR computable para Obra Social (según marca)" v={s4 ? money(s4.nrComputableOS) : "—"} />
            <KV l="Base APORTES seg. social (mín-máx)" v={s4 ? money(s4.baseAportes) : "—"} />
            <KV l="Base CONTRIB. seg. social (solo mín.)" v={s4 ? money(s4.baseContribSoloMin) : "—"} />
            <KV l="Base CONTRIB. neta (menos detracción Dto. 814)" v={s4 ? money(s4.baseContribNeta) : "—"} />
            <KV l="Base mínima Obra Social (piso mín×2, sin techo → Sección 6)" v={s4 ? money(s4.baseMinObraSocial) : "—"} />
            <KV l="Base máxima Obra Social (con techo → aporte Sección 5)" v={s4 ? money(s4.baseMaxObraSocial) : "—"} />
          </div>
        </Sec>

        {/* SECCIÓN 5 — alícuotas editables */}
        <Sec n="5" title="Aportes del trabajador">
          <div className="flex justify-end mb-2">
            <Button type="button" onClick={() => addExtra("aportesExtra")} disabled={readOnly} size="sm" variant="outline" data-testid="add-aporte" className="rounded-none gap-1 h-8"><Plus size={14} /> Agregar Aportes +</Button>
          </div>
          <table className="w-full text-sm xls-grid">
            <thead><tr className="bg-slate-100 text-left">{["Concepto", "Base imponible ($)", "Alícuota", "Importe ($)"].map((h, i) => <th key={i} className={`p-2 label-tiny ${i > 0 ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
            <tbody>
              <ApRow c="Jubilación — SIPA (Ley 24.241)" base={s4?.baseAportes} importe={ap?.sipa} rk="sipa" val={form.ratesEmpleado.sipa} onChange={setRate} testid="al-sipa" />
              <ApRow c="Ley 19.032 — INSSJP / PAMI" base={s4?.baseAportes} importe={ap?.pami} rk="pami" val={form.ratesEmpleado.pami} onChange={setRate} testid="al-pami" />
              <ApRow c="Obra social — (Ley 23.660)" baseEditable readOnly={readOnly} baseVal={form.baseObraSocialOverride} onBaseChange={(v) => { setOsBaseEdited(true); setForm({ ...form, baseObraSocialOverride: v }); }} onBaseReset={() => { setOsBaseEdited(false); setForm({ ...form, baseObraSocialOverride: result?.baseObraSocial != null ? formatAR(result.baseObraSocial) : "" }); }} importe={ap?.osecac} rk="osecac" val={form.ratesEmpleado.osecac} onChange={setRate} testid="al-osecac" />
              {form.tipoConvenio === "comercio" && (<>
                <ApRow c="Aporte solidario FAECYS (art. 100 CCT)" base={result?.sacBruto} importe={ap?.faecys} rk="faecys" val={form.ratesEmpleado.faecys} onChange={setRate} testid="al-faecys" />
                <ApRow c="Cuota sindical SEC (art. 101)" base={result?.sacBruto} importe={ap?.sec} rk="sec" val={form.ratesEmpleado.sec} onChange={setRate} testid="al-sec" />
              </>)}
              {form.tipoConvenio === "otro_convenio" && (
                <tr className="text-slate-600"><td className="p-2">Retención sindical (Otro Convenio)</td>
                  <td className="p-2 text-right tabular">{result ? money(result.sacBruto) : "—"}</td>
                  <td className="p-2 text-right tabular">{pct(form.retencionSindicalOtro || 0)}</td>
                  <td className="p-2 text-right tabular" data-testid="al-otro-imp">{ap ? money(ap.retencionOtro) : "—"}</td></tr>
              )}
              {(form.aportesExtra || []).map((ln, i) => (
                <ExtraRow key={i} ln={ln} importe={ap?.extra?.[i]?.importe} readOnly={readOnly}
                  onChange={(k, v) => setExtra("aportesExtra", i, k, v)} onDel={() => delExtra("aportesExtra", i)} testid={`ap-extra-${i}`} />
              ))}
              <tr className="bg-slate-50 font-bold text-slate-900"><td className="p-2">TOTAL APORTES</td><td /><td /><td className="p-2 text-right tabular" data-testid="total-aportes">{ap ? money(ap.total) : "—"}</td></tr>
            </tbody>
          </table>
          <div className="brand-bg text-white flex justify-between px-3 py-2.5 mt-2" data-testid="calc-neto">
            <span className="label-tiny label-on-dark">AGUINALDO NETO (de bolsillo)</span>
            <span className="font-head font-bold tabular text-lg">{result ? money(result.neto) : "—"}</span>
          </div>
        </Sec>

        {/* SECCIÓN 6 */}
        <Sec n="6" title="Contribuciones patronales (seguridad social desglosada)">
          <div className="flex justify-end mb-2">
            <Button type="button" onClick={() => addExtra("contribucionesExtra")} disabled={readOnly} size="sm" variant="outline" data-testid="add-contrib" className="rounded-none gap-1 h-8"><Plus size={14} /> Agregar Contribuciones</Button>
          </div>
          <table className="w-full text-sm xls-grid">
            <thead><tr className="bg-slate-100 text-left">{["Concepto", "Base imponible ($)", "Alícuota", "Importe ($)"].map((h, i) => <th key={i} className={`p-2 label-tiny ${i > 0 ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
            <tbody>
              {cfg6("sipa").visibleCalc && !form.contribExcluidas.includes("sipa") && <PatRow c="SIPA — Jubilación (Ley 24.241)" base={co?.baseSUSS} rk="sipa" val={patRates.sipa} importe={co?.sipa} onRate={setPatRate} onDel={() => excludeContrib("sipa")} readOnly={readOnly} editable={cfg6("sipa").editable} testid="co-sipa" />}
              {cfg6("inssjp").visibleCalc && !form.contribExcluidas.includes("inssjp") && <PatRow c="INSSJP / PAMI (Ley 19.032)" base={co?.baseSUSS} rk="inssjp" val={patRates.inssjp} importe={co?.inssjp} onRate={setPatRate} onDel={() => excludeContrib("inssjp")} readOnly={readOnly} editable={cfg6("inssjp").editable} testid="co-inssjp" />}
              {cfg6("fne").visibleCalc && !form.contribExcluidas.includes("fne") && <PatRow c="Fondo Nacional de Empleo (Ley 24.013)" base={co?.baseSUSS} rk="fne" val={patRates.fne} importe={co?.fne} onRate={setPatRate} onDel={() => excludeContrib("fne")} readOnly={readOnly} editable={cfg6("fne").editable} testid="co-fne" />}
              {cfg6("af").visibleCalc && !form.contribExcluidas.includes("af") && <PatRow c="Asignaciones Familiares (Ley 24.714)" base={co?.baseSUSS} rk="af" val={patRates.af} importe={co?.af} onRate={setPatRate} onDel={() => excludeContrib("af")} readOnly={readOnly} editable={cfg6("af").editable} testid="co-af" />}
              {cfg6("osecac").visibleCalc && !form.contribExcluidas.includes("osecac") && (
                <tr className="text-slate-600">
                  <td className="p-2">Obra social — (Ley 23.660)</td>
                  <td className="p-1 text-right tabular">
                    <div className="flex items-center gap-1 justify-end">
                      <ARMoneyInput testid="co-osecac-base" disabled={readOnly || !cfg6("osecac").editable} className="xls-input text-right h-8 w-40" value={form.baseObraSocial6Override ?? ""} onChange={(v) => { setOs6BaseEdited(true); setForm({ ...form, baseObraSocial6Override: v }); }} />
                      <button type="button" title="Volver al valor calculado" data-testid="co-osecac-base-reset" disabled={readOnly || !cfg6("osecac").editable} onClick={() => { setOs6BaseEdited(false); setForm({ ...form, baseObraSocial6Override: co?.obraSocial6BaseDefault != null ? formatAR(co.obraSocial6BaseDefault) : "" }); }} className="text-slate-400 hover:text-slate-700 disabled:opacity-40"><RotateCcw size={13} /></button>
                    </div>
                  </td>
                  <td className="p-1 text-right"><PctInput testid="al-os-patronal" value={form.obraSocialEmpleador} onChange={(v) => setForm({ ...form, obraSocialEmpleador: v })} disabled={readOnly || !cfg6("osecac").editable} compact /></td>
                  <td className="p-2 text-right tabular"><div className="flex items-center justify-end gap-2"><span>{co ? money(co.obraSocial6) : "—"}</span>{!readOnly && cfg6("osecac").editable && <button type="button" onClick={() => excludeContrib("osecac")} data-testid="co-osecac-del" className="text-destructive hover:opacity-70"><X size={14} /></button>}</div></td>
                </tr>
              )}
              {cfg6("art").visibleCalc && !form.contribExcluidas.includes("art") && <PatRow c="A.R.T. (Ley 24.557)" baseEditable readOnly={readOnly} baseVal={form.baseArtOverride} onBaseChange={(v) => { setArtBaseEdited(true); setForm({ ...form, baseArtOverride: v }); }} onBaseReset={() => { setArtBaseEdited(false); setForm({ ...form, baseArtOverride: co?.artBaseDefault != null ? formatAR(co.artBaseDefault) : "" }); }} rk="art" val={artRate} importe={co?.art} fixedRate onRate={setPatRate} onDel={() => excludeContrib("art")} editable={cfg6("art").editable} testid="co-art" />}
              {cfg6("scvo").visibleCalc && !form.contribExcluidas.includes("scvo") && <PatRow c="S.C.V.O. — Seguro de vida obligatorio (Dto. 1567/74)" base={co && co.scvoRate ? co.scvo / co.scvoRate : null} rk="scvo" val={scvoRate} importe={co?.scvo} fixedRate onRate={setPatRate} onDel={() => excludeContrib("scvo")} readOnly={readOnly} editable={cfg6("scvo").editable} testid="co-scvo" />}
              {(form.contribucionesExtra || []).map((ln, i) => (
                <ExtraRow key={i} ln={ln} importe={co?.extra?.[i]?.importe} readOnly={readOnly}
                  onChange={(k, v) => setExtra("contribucionesExtra", i, k, v)} onDel={() => delExtra("contribucionesExtra", i)} testid={`co-extra-${i}`} />
              ))}
              <tr className="bg-slate-50 font-bold text-slate-900"><td className="p-2">TOTAL CONTRIBUCIONES</td><td /><td /><td className="p-2 text-right tabular" data-testid="total-contrib">{co ? money(co.total) : "—"}</td></tr>
              <tr className="bg-slate-50 font-bold text-slate-900"><td className="p-2">COSTO TOTAL DEL SAC (empleador)</td><td /><td /><td className="p-2 text-right tabular" data-testid="costo-total">{co ? money(co.costoTotal) : "—"}</td></tr>
            </tbody>
          </table>
        </Sec>

        <Sec n="" title="Notas y referencias">
          <ul className="space-y-1.5 text-xs text-muted-foreground leading-relaxed list-disc pl-4">
            {NOTAS.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </Sec>
      </fieldset>
      <CrossReplaceDialog
        open={!!crossSel}
        otherLabel="Recibo de Sueldo"
        currentLabel="Aguinaldo"
        onConfirm={() => { applyLegajo(crossSel); setCrossSel(null); }}
        onCancel={() => setCrossSel(null)}
      />
      <StaleDataDialog
        open={!!staleParams}
        title="Parámetros actualizados"
        intro={`El administrador actualizó los parámetros del período (Semestre ${form.semestreHalf} ${form.semestreYear}) mientras trabajabas. Para continuar, el cálculo debe usar los valores nuevos.`}
        rows={staleRows}
        note="Los datos que cargaste (sueldos, meses, empleado) no se pierden. Solo se actualizan estos valores de referencia."
        buttonText="Actualizar valores y recalcular"
        onApply={applyFreshParams}
        applying={loading}
      />
    </AppLayout>
  );
}

const Sec = ({ n, title, children }) => (
  <section className="bg-white">
    <div className="px-5 py-3 brand-bg"><span className="label-tiny label-on-dark">{n ? `${n}) ` : ""}{title}</span></div>
    <div className="p-5">{children}</div>
  </section>
);
const ReadF = ({ label, value, testid }) => (
  <div>
    <Label className="label-tiny">{label}</Label>
    <div className="mt-1.5 h-10 px-3 flex items-center border border-border bg-slate-50 text-sm text-slate-900" data-testid={testid}>{value || "—"}</div>
  </div>
);
const KV = ({ l, v }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-border text-sm">
    <span className="text-slate-600">{l}</span><span className="tabular font-medium text-slate-900 whitespace-nowrap">{v}</span>
  </div>
);
const KVedit = ({ l, children }) => (
  <div className="flex justify-between items-center gap-4 py-1.5 border-b border-border text-sm">
    <span className="text-slate-600">{l}</span><div className="w-28">{children}</div>
  </div>
);
// Percent input: shows %, stores decimal
const PctInput = ({ value, onChange, testid, compact, disabled }) => (
  <div className="flex items-center gap-1 justify-end">
    <ARPctInput value={value} onChange={onChange} testid={testid} disabled={disabled}
      className={`xls-input text-right ${compact ? "h-8 w-24" : "h-9"}`} />
    <span className="text-slate-500">%</span>
  </div>
);
const MoneyInput = ({ value, onChange, testid }) => (
  <ARMoneyInput value={value} onChange={onChange} testid={testid} />
);
// AR-formatted money input: free typing while focused, formats (dot-thousands, comma-decimals) on blur
const ARMoneyInput = ({ value, onChange, testid, compact, disabled, className }) => {
  const handleChange = (e) => {
    const v = e.target.value;
    if (/^[0-9.,]*$/.test(v)) onChange(v);
  };
  const handleBlur = () => onChange(value === "" ? "" : formatAR(parseAR(value)));
  return (
    <Input type="text" inputMode="decimal" placeholder="0,00" data-testid={testid} disabled={disabled}
      className={className || `xls-input text-right ${compact ? "h-9" : "h-9"}`}
      value={value ?? ""} onChange={handleChange} onBlur={handleBlur} />
  );
};
const Empty = () => null;
// AR-formatted percentage input: text field (numeric keypad friendly), accepts comma OR period
// as decimal separator, stores the value as a fraction (rate), displays with comma (es-AR).
const ARPctInput = ({ value, onChange, testid, disabled, className }) => {
  const [draft, setDraft] = useState(null);
  const formatted = value === "" || value == null ? ""
    : Number((Number(value) * 100).toFixed(4)).toLocaleString("es-AR", { maximumFractionDigits: 4, useGrouping: false });
  const shown = draft != null ? draft : formatted;
  return (
    <Input type="text" inputMode="decimal" data-testid={testid} disabled={disabled} className={className}
      title={shown === "" ? "" : `${shown}%`} value={shown}
      onChange={(e) => { const v = e.target.value; if (/^[0-9.,]*$/.test(v)) { setDraft(v); onChange(v === "" ? "" : parseAR(v) / 100); } }}
      onBlur={() => setDraft(null)} />
  );
};
const ApRow = ({ c, base, importe, rk, val, onChange, testid, baseEditable, baseVal, onBaseChange, onBaseReset, readOnly }) => (
  <tr className="text-slate-600">
    <td className="p-2">{c}</td>
    <td className="p-1 text-right tabular">
      {baseEditable ? (
        <div className="flex items-center gap-1 justify-end">
          <ARMoneyInput testid={`${testid}-base`} disabled={readOnly} className="xls-input text-right h-8 w-40" value={baseVal ?? ""} onChange={onBaseChange} />
          {onBaseReset && <button type="button" title="Volver al valor calculado" data-testid={`${testid}-base-reset`} disabled={readOnly} onClick={onBaseReset} className="text-slate-400 hover:text-slate-700 disabled:opacity-40"><RotateCcw size={13} /></button>}
        </div>
      ) : (base != null ? money(base) : "—")}
    </td>
    <td className="p-1 text-right">
      <div className="flex items-center gap-1 justify-end">
        <ARPctInput value={val} onChange={(v) => onChange(rk, v)} testid={testid} className="xls-input text-right h-8 w-24" />
        <span className="text-slate-500">%</span>
      </div>
    </td>
    <td className="p-2 text-right tabular">{importe != null ? money(importe) : "—"}</td>
  </tr>
);
const CoRow = ({ c, base, alic, importe }) => (
  <tr className="text-slate-600">
    <td className="p-2">{c}</td>
    <td className="p-2 text-right tabular">{base != null ? money(base) : "—"}</td>
    <td className="p-2 text-right tabular">{alic != null ? pct(alic) : "—"}</td>
    <td className="p-2 text-right tabular">{importe != null ? money(importe) : "—"}</td>
  </tr>
);
const PatRow = ({ c, base, rk, val, importe, onRate, onDel, readOnly, editable = true, testid, baseEditable, baseVal, onBaseChange, onBaseReset }) => (
  <tr className="text-slate-600">
    <td className="p-2">{c}</td>
    <td className="p-1 text-right tabular">
      {baseEditable ? (
        <div className="flex items-center gap-1 justify-end">
          <ARMoneyInput testid={`${testid}-base`} disabled={readOnly} className="xls-input text-right h-8 w-40" value={baseVal ?? ""} onChange={onBaseChange} />
          {onBaseReset && <button type="button" title="Volver al valor calculado" data-testid={`${testid}-base-reset`} disabled={readOnly} onClick={onBaseReset} className="text-slate-400 hover:text-slate-700 disabled:opacity-40"><RotateCcw size={13} /></button>}
        </div>
      ) : (base != null ? money(base) : "—")}
    </td>
    <td className="p-1 text-right">
      <div className="flex items-center gap-1 justify-end">
        <ARPctInput value={val} onChange={(v) => onRate(rk, v === "" ? 0 : v)} testid={`${testid}-alic`} disabled={readOnly || !editable} className="xls-input text-right h-8 w-24" />
        <span className="text-slate-500">%</span>
      </div>
    </td>
    <td className="p-2 text-right tabular">
      <div className="flex items-center justify-end gap-2">
        <span>{importe != null ? money(importe) : "—"}</span>
        {!readOnly && editable && <button type="button" onClick={onDel} data-testid={`${testid}-del`} className="text-destructive hover:opacity-70"><X size={14} /></button>}
      </div>
    </td>
  </tr>
);
const ExtraRow = ({ ln, importe, onChange, onDel, readOnly, testid }) => {
  const ed = ln.editable !== false;
  return (
  <tr className="text-slate-600 bg-amber-50/40">
    <td className="p-1 align-top">
      <Input type="text" placeholder="Concepto" data-testid={`${testid}-concepto`} disabled={readOnly || !ed}
        className="xls-input h-8" value={ln.concepto} onChange={(e) => onChange("concepto", e.target.value)} />
    </td>
    <td className="p-1 align-top">
      <Input type="text" inputMode="decimal" placeholder="0,00" data-testid={`${testid}-base`} disabled={readOnly || !ed}
        className="xls-input text-right h-8" value={ln.base}
        onChange={(e) => { if (/^[0-9.,]*$/.test(e.target.value)) onChange("base", e.target.value); }}
        onBlur={() => onChange("base", ln.base === "" ? "" : formatAR(parseAR(ln.base)))} />
    </td>
    <td className="p-1 text-right align-top">
      <div className="flex items-center gap-1 justify-end">
        <ARPctInput value={ln.alicuota} onChange={(v) => onChange("alicuota", v)} testid={`${testid}-alicuota`} disabled={readOnly || !ed} className="xls-input text-right h-8 w-24" />
        <span className="text-slate-500">%</span>
      </div>
    </td>
    <td className="p-2 text-right tabular align-top">
      <div className="flex items-center justify-end gap-2">
        <span data-testid={`${testid}-imp`}>{importe != null ? money(importe) : "—"}</span>
        {!readOnly && <button type="button" onClick={onDel} data-testid={`${testid}-del`} className="text-destructive hover:opacity-70"><X size={14} /></button>}
      </div>
    </td>
  </tr>
  );
};
